import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';

// Demo inventory data - completely separate from production
const demoCategories = [
  { id: 'small', name: 'Small Idols', range: '1-30 Inch' },
  { id: 'medium', name: 'Medium Idols', range: '31-60 Inch' },
  { id: 'large', name: 'Large Idols', range: '61-90 Inch' }
];

const demoInventory = [
  {
    id: 'demo-shop-1',
    modelName: 'Traditional Ganesh Idol',
    modelNumber: 'G001',
    size: '12 Inch',
    price: 2500,
    category: 'medium',
    currentStock: 15,
    initialStock: 20,
    photo: '/gpm.png',
    stockStatus: 'good',
    stockText: 'In Stock',
    description: 'Beautiful traditional Ganesh idol with intricate carvings'
  },
  {
    id: 'demo-shop-2',
    modelName: 'Mini Ganesh Idol',
    modelNumber: 'G004',
    size: '6 Inch',
    price: 1200,
    category: 'small',
    currentStock: 25,
    initialStock: 30,
    photo: '/gpm.png',
    stockStatus: 'good',
    stockText: 'In Stock',
    description: 'Perfect for home shrines and small spaces'
  },
  {
    id: 'demo-shop-3',
    modelName: 'Designer Ganesh Murti',
    modelNumber: 'G006',
    size: '10 Inch',
    price: 3200,
    category: 'medium',
    currentStock: 12,
    initialStock: 15,
    photo: '/gpm.png',
    stockStatus: 'good',
    stockText: 'In Stock',
    description: 'Modern design with traditional craftsmanship'
  },
  {
    id: 'demo-shop-4',
    modelName: 'Large Ganesh Statue',
    modelNumber: 'G005',
    size: '24 Inch',
    price: 8500,
    category: 'large',
    currentStock: 3,
    initialStock: 5,
    photo: '/gpm.png',
    stockStatus: 'low',
    stockText: 'Low Stock',
    description: 'Impressive large statue perfect for temples and events'
  },
  {
    id: 'demo-shop-5',
    modelName: 'Elegant Ganesh Idol',
    modelNumber: 'G007',
    size: '8 Inch',
    price: 1800,
    category: 'small',
    currentStock: 20,
    initialStock: 25,
    photo: '/gpm.png',
    stockStatus: 'good',
    stockText: 'In Stock',
    description: 'Elegant and simple design for modern homes'
  },
  {
    id: 'demo-shop-6',
    modelName: 'Premium Ganesh Statue',
    modelNumber: 'G008',
    size: '18 Inch',
    price: 6500,
    category: 'large',
    currentStock: 2,
    initialStock: 4,
    photo: '/gpm.png',
    stockStatus: 'low',
    stockText: 'Low Stock',
    description: 'Premium quality with gold accents and detailed work'
  }
];

const DemoShopPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('medium');
  const [inventory, setInventory] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [checkoutForm, setCheckoutForm] = useState({
    customerName: '',
    phone: '',
    deliveryDate: '',
    advanceAmount: 0
  });
  const [itemForm, setItemForm] = useState({
    quantity: 1,
    customRequest: ''
  });
  const [cartExpanded, setCartExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [wishlist, setWishlist] = useState([]);
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    image: null,
    zoom: 1,
    panX: 0,
    panY: 0,
    isDragging: false,
    dragStart: { x: 0, y: 0 }
  });
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const navigate = useNavigate();

  // Load demo cart and wishlist from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('demoShopCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          setCart(parsedCart);
          toast.success('Your demo cart items have been restored!', {
            duration: 3000,
            icon: '🛒'
          });
        }
      } catch (error) {
        console.error('Error parsing demo cart data:', error);
        localStorage.removeItem('demoShopCart');
      }
    }

    const savedWishlist = localStorage.getItem('demoShopWishlist');
    if (savedWishlist) {
      try {
        const parsedWishlist = JSON.parse(savedWishlist);
        if (Array.isArray(parsedWishlist)) {
          setWishlist(parsedWishlist);
        }
      } catch (error) {
        console.error('Error parsing demo wishlist data:', error);
        localStorage.removeItem('demoShopWishlist');
      }
    }
  }, []);

  // Save demo cart to localStorage
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem('demoShopCart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('demoShopCart');
    }
  }, [cart]);

  // Save demo wishlist to localStorage
  useEffect(() => {
    if (wishlist.length > 0) {
      localStorage.setItem('demoShopWishlist', JSON.stringify(wishlist));
    } else {
      localStorage.removeItem('demoShopWishlist');
    }
  }, [wishlist]);

  // Load inventory for selected category
  useEffect(() => {
    const categoryItems = demoInventory.filter(item => item.category === selectedCategory);
    let sortedItems = [...categoryItems];

    // Apply sorting
    sortedItems.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
        default:
          return a.modelName.localeCompare(b.modelName);
      }
    });

    setInventory(sortedItems);
  }, [selectedCategory, sortBy]);

  const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setItemForm({
      quantity: 1,
      customRequest: ''
    });
    setImageViewer({
      isOpen: true,
      image: item.photo,
      zoom: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStart: { x: 0, y: 0 }
    });
  };

  const handleViewerClose = () => {
    setImageViewer({
      isOpen: false,
      image: null,
      zoom: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStart: { x: 0, y: 0 }
    });
  };

  const handleMouseWheel = (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(imageViewer.zoom * zoomFactor, 0.5), 5);
    setImageViewer(prev => ({ ...prev, zoom: newZoom }));
  };

  const handleMouseDown = (e) => {
    setImageViewer(prev => ({
      ...prev,
      isDragging: true,
      dragStart: { x: e.clientX - prev.panX, y: e.clientY - prev.panY }
    }));
  };

  const handleMouseMove = (e) => {
    if (imageViewer.isDragging) {
      const newPanX = e.clientX - imageViewer.dragStart.x;
      const newPanY = e.clientY - imageViewer.dragStart.y;
      setImageViewer(prev => ({
        ...prev,
        panX: newPanX,
        panY: newPanY
      }));
    }
  };

  const handleMouseUp = () => {
    setImageViewer(prev => ({ ...prev, isDragging: false }));
  };

  const handleDoubleClick = () => {
    setImageViewer(prev => ({ ...prev, zoom: 1, panX: 0, panY: 0 }));
  };

  // Keyboard controls for image viewer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (imageViewer.isOpen) {
        if (e.key === 'Escape') {
          handleViewerClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageViewer.isOpen]);

  const handleQuantityChange = (quantity) => {
    const qty = Math.max(1, Math.min(quantity, selectedItem?.currentStock || 1));
    setItemForm(prev => ({ ...prev, quantity: qty }));
  };

  const calculateItemTotal = () => {
    return (selectedItem?.price || 0) * (itemForm.quantity || 1);
  };

  const calculateGrandTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTotalAdvance = () => {
    return cart.reduce((sum, item) => sum + item.advanceAmount, 0);
  };

  const calculateBalance = () => {
    return calculateGrandTotal() - calculateTotalAdvance();
  };

  const handleAddToCart = () => {
    if (itemForm.quantity > selectedItem.currentStock) {
      toast.error('Not enough stock available in demo inventory');
      return;
    }

    const cartItem = {
      ...selectedItem,
      quantity: itemForm.quantity,
      customRequest: itemForm.customRequest,
      advanceAmount: 0,
      customerName: '',
      phone: '',
      deliveryDate: ''
    };

    setCart(prev => [...prev, cartItem]);
    setShowModal(false);
    setSelectedItem(null);
    toast.success('Demo item added to cart! 🛒');
  };

  const handleCheckout = () => {
    const totalAmount = calculateGrandTotal();
    const minimumAdvance = Math.ceil(totalAmount * 0.4);

    setCheckoutForm({
      customerName: '',
      phone: '',
      deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      advanceAmount: minimumAdvance
    });
    setShowCheckoutModal(true);
  };

  const handleCheckoutSubmit = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error('Valid 10-digit phone number required');
      return;
    }

    const totalAmount = calculateGrandTotal();
    const minimumAdvance = Math.ceil(totalAmount * 0.4);

    if (checkoutForm.advanceAmount < minimumAdvance) {
      toast.error(`Advance payment must be at least ${formatCurrency(minimumAdvance)} (40% of total order value)`);
      return;
    }

    // Apply checkout details to cart items
    const updatedCart = cart.map(item => ({
      ...item,
      customerName: checkoutForm.customerName,
      phone: checkoutForm.phone,
      deliveryDate: checkoutForm.deliveryDate,
      advanceAmount: Math.floor(checkoutForm.advanceAmount / cart.length)
    }));

    // Adjust last item for rounding
    const totalDistributed = updatedCart.slice(0, -1).reduce((sum, item) => sum + item.advanceAmount, 0);
    updatedCart[updatedCart.length - 1].advanceAmount = checkoutForm.advanceAmount - totalDistributed;

    setCart(updatedCart);
    setShowCheckoutModal(false);

    // Mock demo payment process
    handleDemoPayment(checkoutForm.advanceAmount);
  };

  const handleDemoPayment = async (advanceAmount) => {
    toast.success('🎭 Demo Payment Successful!', {
      duration: 3000
    });

    // Generate mock receipt
    const receiptData = {
      orderNo: `DEMO-${Date.now()}`,
      customerName: cart[0].customerName,
      phone: cart[0].phone,
      items: cart,
      totalAmount: calculateGrandTotal(),
      advanceAmount: advanceAmount,
      balanceAmount: calculateGrandTotal() - advanceAmount,
      orderDate: new Date().toLocaleDateString()
    };

    // Create mock PDF receipt (just text for demo)
    const receiptText = `
DEMO ORDER RECEIPT
==================

Order No: ${receiptData.orderNo}
Date: ${receiptData.orderDate}

Customer: ${receiptData.customerName}
Phone: ${receiptData.phone}

Items:
${cart.map(item => `${item.modelName} (x${item.quantity}) - ${formatCurrency(item.price * item.quantity)}`).join('\n')}

Total Amount: ${formatCurrency(receiptData.totalAmount)}
Advance Paid: ${formatCurrency(receiptData.advanceAmount)}
Balance Due: ${formatCurrency(receiptData.balanceAmount)}

Thank you for your demo order!
This is a demonstration receipt only.

🏪 EkDant Arts - Demo Mode
    `.trim();

    // Download text receipt
    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Demo_Receipt_${receiptData.orderNo}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Clear demo cart
    setCart([]);
    localStorage.removeItem('demoShopCart');

    setTimeout(() => {
      toast.success('Demo order completed! Check your downloads for the receipt.', {
        duration: 5000
      });
    }, 1000);
  };

  const handleRemoveFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Toaster position="top-right" />

      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 text-center text-sm font-medium">
        🎭 DEMO MODE - This is a demonstration of your shop. No real orders or payments will be processed.
      </div>

      {/* Header */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-neutral-50 to-sage-50">
        <div className="relative">
          {/* Simple Header */}
          <div className="bg-white border-b border-gray-200 py-6">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">🛍️ Demo Shop</h1>
                  <p className="text-gray-600 text-sm sm:text-base">Experience Your Online Store</p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate('/demo')}
                    className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2"
                  >
                    ← Back to Demo
                  </button>
                  <img
                    src="/logo.png"
                    alt="Ekdant Arts"
                    className="w-12 h-12 sm:w-16 sm:h-16"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Category Selection */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-elegant border border-slate-200/50 mx-4 my-8 p-6">
            <div className="container mx-auto px-4">
              <div className="flex justify-between items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Choose Idol Size:</label>
                  <div className="flex flex-wrap gap-2">
                    {demoCategories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {category.name}
                        {category.range && <span className="block text-xs opacity-75">({category.range})</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sort & Wishlist */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Sort:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    >
                      <option value="name">Name (A-Z)</option>
                      <option value="price-low">Price (Low to High)</option>
                      <option value="price-high">Price (High to Low)</option>
                    </select>
                  </div>

                  <button
                    onClick={() => setShowWishlistModal(true)}
                    className="relative p-3 bg-red-100 hover:bg-red-200 rounded-lg transition-colors border-2 border-red-200 hover:border-red-300"
                    title="View Demo Wishlist"
                  >
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {wishlist.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                        {wishlist.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cart Section */}
          {cart.length > 0 && (
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-elegant border border-slate-200/50 mx-4 mb-8">
              <div className="container mx-auto px-4 py-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div className="flex-1 flex items-center gap-4">
                    <div className="bg-sage-100 p-2 rounded-lg">
                      <span className="text-2xl">🛒</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Demo Cart ({cart.length} items)</h3>
                      <p className="text-sm text-gray-600">Total: {formatCurrency(calculateGrandTotal())}</p>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    className="bg-orange-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-orange-600 transition-colors"
                  >
                    🛒 Demo Checkout
                  </button>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                  {cart.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                      <img src={item.photo} alt={item.modelName} className="w-12 h-12 object-cover rounded" />
                      <div className="flex-1">
                        <p className="font-semibold">{item.modelName}</p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity} × {formatCurrency(item.price)}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveFromCart(index)}
                        className="text-red-500 hover:text-red-700 text-xl flex-shrink-0"
                        title="Remove from demo cart"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Product Grid */}
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {inventory.map(item => {
                const isWishlisted = wishlist.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  >
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={item.photo}
                        alt={item.modelName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />

                      {/* Wishlist Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setWishlist(prev =>
                            isWishlisted
                              ? prev.filter(id => id !== item.id)
                              : [...prev, item.id]
                          );
                          toast.success(isWishlisted ? 'Removed from demo wishlist' : 'Added to demo wishlist ❤️');
                        }}
                        className="absolute top-3 left-3 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
                      >
                        <svg
                          className={`w-5 h-5 ${isWishlisted ? 'text-red-500 fill-current' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </button>

                      {/* Stock Badge */}
                      <div className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-semibold ${
                        item.stockStatus === 'low'
                          ? 'bg-red-500 text-white'
                          : 'bg-green-500 text-white'
                      }`}>
                        {item.stockText}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <div className="mb-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Model {item.modelNumber}</span>
                      </div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2">{item.modelName}</h3>
                      <div className="flex items-center justify-between">
                        <p className="text-2xl font-bold text-orange-600">{formatCurrency(item.price)}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                            setItemForm({
                              quantity: 1,
                              customRequest: ''
                            });
                            setShowModal(true);
                          }}
                          className="bg-orange-500 text-white p-2 rounded-lg hover:bg-orange-600 transition-colors"
                          title="Add to Demo Cart"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l-1.5 3m3-3l1.5-3M12 13v6m0 0h3m-3 0h-3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Order Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[95vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900 pr-2">
                  Demo Order: {selectedItem.modelName}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl flex-shrink-0 p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <img
                    src={selectedItem.photo}
                    alt={selectedItem.modelName}
                    className="w-full rounded-lg shadow-md mb-4"
                  />
                  <p className="text-sm text-gray-600">{selectedItem.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    min="1"
                    max={selectedItem.currentStock}
                    value={itemForm.quantity}
                    onChange={(e) => handleQuantityChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Total: {formatCurrency(calculateItemTotal())}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Request</label>
                  <textarea
                    value={itemForm.customRequest}
                    onChange={(e) => setItemForm(prev => ({ ...prev, customRequest: e.target.value }))}
                    placeholder="Any special requirements..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows="2"
                  />
                </div>

                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">
                    🎭 <strong>DEMO MODE:</strong> This is a demonstration. Customer details will be collected during demo checkout.
                  </p>
                </div>

                <button
                  onClick={handleAddToCart}
                  className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-orange-600 transition-colors"
                >
                  Add to Demo Cart 🛒
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[95vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900 pr-2">
                  Demo Checkout
                </h2>
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl flex-shrink-0 p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-6">
                <p className="text-sm text-purple-800">
                  🎭 <strong>DEMO MODE:</strong> This checkout is for demonstration only. No real payment will be processed.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={checkoutForm.customerName}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, customerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Demo Customer Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="9876543210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Demo Advance Payment (₹) *</label>
                  <input
                    type="number"
                    min="1"
                    max={calculateGrandTotal()}
                    value={checkoutForm.advanceAmount}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, advanceAmount: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Balance due: {formatCurrency(calculateGrandTotal() - checkoutForm.advanceAmount)}
                  </p>
                </div>

                <button
                  onClick={handleCheckoutSubmit}
                  className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-orange-600 transition-colors"
                >
                  Complete Demo Order 🎭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist Modal */}
      {showWishlistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Demo Wishlist ❤️ ({wishlist.length})
                </h2>
                <button
                  onClick={() => setShowWishlistModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              {wishlist.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🤍</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Your demo wishlist is empty</h3>
                  <p className="text-gray-600 mb-6">Start browsing demo products and add items you love!</p>
                  <button
                    onClick={() => setShowWishlistModal(false)}
                    className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Continue Demo Shopping
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto max-h-96">
                  {wishlist.map(itemId => {
                    const item = demoInventory.find(inv => inv.id === itemId);
                    if (!item) return null;

                    return (
                      <div
                        key={itemId}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={item.photo}
                            alt={item.modelName}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="p-4">
                          <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2">{item.modelName}</h3>
                          <div className="flex items-center justify-between">
                            <p className="text-2xl font-bold text-orange-600">{formatCurrency(item.price)}</p>
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setItemForm({
                                  quantity: 1,
                                  customRequest: ''
                                });
                                setShowModal(true);
                                setShowWishlistModal(false);
                              }}
                              className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm font-semibold"
                            >
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Image Viewer */}
      {imageViewer.isOpen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={handleViewerClose}
            className="absolute top-4 right-4 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="absolute top-4 left-4 z-10 text-white/70 text-sm">
            <div>🎭 DEMO MODE - Zoom & Pan Demo</div>
          </div>

          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
            onWheel={handleMouseWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={handleDoubleClick}
          >
            <img
              src={imageViewer.image}
              alt="Demo Product"
              className="max-w-full max-h-full object-contain select-none transition-transform duration-200"
              style={{
                transform: `scale(${imageViewer.zoom}) translate(${imageViewer.panX / imageViewer.zoom}px, ${imageViewer.panY / imageViewer.zoom}px)`,
                transformOrigin: 'center center'
              }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DemoShopPage;
